import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';
import WaveLoader from '../components/WaveLoader';
import EntityMultiSelect from '../components/EntityMultiSelect';

import { typeLabel, tokenLabel } from '../utils/entityLabels';

import WeekNote from '../components/WeekNote';

function InsightCard({ label, title, body }) {
  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      borderRadius: '24px', padding: '24px', boxShadow: 'var(--shadow-md)',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#8b7355', marginBottom: '12px' }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-dark)', lineHeight: 1.3, marginBottom: '12px' }}>
        {title}
      </div>
      <p style={{ fontSize: '14px', lineHeight: 1.75, color: 'var(--text-light)', margin: 0 }}>{body}</p>
    </div>
  );
}

function KpiCard({ label, value, note, accent = 'blue' }) {
  const colors = { blue: '#1f5eff', green: '#1f7a3f', amber: '#c98b12', teal: '#0f766e', red: '#c2410c' };
  return (
    <div className="metric-card">
      <span className="metric-label">{label}</span>
      <div className="metric-value" style={{ color: colors[accent] || colors.blue }}>{value}</div>
      {note && <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '6px' }}>{note}</div>}
    </div>
  );
}

function SmallList({ rows, emptyLabel, nameKey = 'entity_name', countKey = 'mention_count', subtitleKey = 'entity_type' }) {
  if (!rows.length) return (
    <div style={{ border: '1.5px dashed var(--card-border)', borderRadius: '14px', padding: '24px 12px', textAlign: 'center', fontSize: '13px', color: 'var(--text-lighter)' }}>
      {emptyLabel}
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {rows.map(row => (
        <div key={`${row[nameKey]}-${row[subtitleKey] || ''}`} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'var(--bg-dark)', border: '1px solid var(--card-border)',
          borderRadius: '12px', padding: '10px 14px',
        }}>
          <div>
            <span style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '13px' }}>{row[nameKey]}</span>
            {subtitleKey && row[subtitleKey] && <span style={{ marginLeft: '6px', fontSize: '12px', color: 'var(--text-lighter)' }}>({row[subtitleKey]})</span>}
          </div>
          <span style={{ fontSize: '13px', color: 'var(--text-light)', fontWeight: 500 }}>{row[countKey]}</span>
        </div>
      ))}
    </div>
  );
}

const COLORS = ['#1f5eff', '#1f7a3f', '#c98b12', '#c2410c', '#7c3aed', '#0f766e', '#1a4fd6', '#c2410c'];
// Mirrors TREND_ENTITY_LIMIT in routes/sales_pages.py — the server truncates past this,
// so the picker stops offering additions there rather than silently dropping them.
const TREND_ENTITY_MAX = 8;
// Ceiling on Explorer picks, mirrored by EXPLORER_ENTITY_MAX in routes/sales_pages.py.
const EXPLORER_ENTITY_MAX = 25;

function FieldIntel({ filters, loading, setLoading }) {
  const fetchIdRef = React.useRef(0);
  const explorerFetchIdRef = React.useRef(0);
  // The fetchId guards below only ignore stale RESPONSES — the server still runs every
  // superseded request to completion. /api/field-intelligence issues ~11 queries, so a
  // burst of them can occupy all gunicorn workers and starve later requests. Aborting
  // frees the worker as soon as the result stops mattering.
  const abortRef = React.useRef(null);
  const explorerAbortRef = React.useRef(null);
  const trendFetchIdRef = React.useRef(0);
  const trendAbortRef = React.useRef(null);
  const [pageData, setPageData] = useState({
    summary: {}, entityTypes: [], rankedEntities: [], trends: [],
    marketWatch: {},
    explorer: { availableTypes: [], entityNames: [] },
  });
  const [explorerData, setExplorerData] = useState({ trend: [], byRegion: [], byCity: [], cooccurring: [] });
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  // The Explorer pools its picks into one subject rather than comparing them, so this
  // is a set of names, not an ordered comparison list. See the endpoint docstring.
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [watchTypes, setWatchTypes] = useState([]);
  // Trend Comparison drives its own fetch so its controls refresh one chart, not the page.
  // trendEntities holds the user's EXPLICIT picks; empty means "let the server choose the
  // top few". What is actually charted is always trendData.selected, so the chips cannot
  // drift from the lines.
  const [trendType, setTrendType] = useState('');
  // null = "let the server seed the strongest entities"; an array = the user's explicit
  // selection, and [] genuinely means "none", which is why this is not just an empty array.
  const [trendEntities, setTrendEntities] = useState(null);
  const [trendData, setTrendData] = useState({ trends: [], selected: [], available_types: [], available_entities: [] });
  const [trendLoading, setTrendLoading] = useState(false);
  const [locationMode, setLocationMode] = useState('region');
  const [loadError, setLoadError] = useState(null);

  // Co-occurring entities grouped by the org's own entity_type values (crop, brand,
  // everything else) rather than a hardcoded vocabulary — entity types differ per org.
  // Types are ordered by total mentions, and untyped rows collect under "Other".
  const cooccurringGroups = useMemo(() => {
    const groups = {};
    (explorerData.cooccurring || []).forEach(row => {
      // 'nan' is a real stored value in dim_entity.entity_type (every other query in
      // sales_pages.py filters it out). It only became visible once the type turned into
      // a heading, so fold it into Other rather than titling a group "nan".
      const raw = (row.entity_type || '').trim();
      const type = (!raw || raw.toLowerCase() === 'nan') ? 'Other' : raw;
      (groups[type] = groups[type] || []).push(row);
    });
    const total = rows => rows.reduce((s, r) => s + (r.mention_count || 0), 0);
    // Rank on the FULL group before trimming to 8 for display — ranking on the trimmed
    // list would understate a broad type that spreads its mentions over many entities.
    return Object.entries(groups)
      .sort((a, b) => {
        if (a[0] === 'Other') return 1;   // "Other" is a catch-all, always last
        if (b[0] === 'Other') return -1;
        return total(b[1]) - total(a[1]);
      })
      .map(([type, rows]) => [type, rows.slice(0, 8)]);
  }, [explorerData.cooccurring]);

  const fetchData = useCallback((wtOverride) => {
    const id = ++fetchIdRef.current;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });
    const wt = wtOverride || watchTypes;
    if (wt.length) params.append('watch_types', wt.join(','));

    axios.get(`/api/field-intelligence?${params}`, { signal: ctrl.signal })
      .then(res => {
        if (id !== fetchIdRef.current) return;
        if (res.data && res.data.error) throw new Error(res.data.error);
        setLoadError(null);
        setPageData(res.data);
        if (!wt.length && res.data.marketWatch) {
          setWatchTypes(Object.keys(res.data.marketWatch));
        }
      })
      .catch(err => {
        if (id !== fetchIdRef.current || axios.isCancel(err)) return;
        console.error('Error loading Field Intelligence:', err);
        // A failed fetch used to leave pageData at its zero-initialized state, so the
        // page silently rendered "no data" cards instead of showing anything went wrong —
        // indistinguishable from a genuinely empty scope. Surface it instead.
        setLoadError(err.response?.data?.error || err.message || 'Failed to load Field Intelligence data.');
      })
      .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
  }, [filters, setLoading, watchTypes]);

  const fetchExplorerData = useCallback((entities) => {
    const id = ++explorerFetchIdRef.current;
    if (explorerAbortRef.current) explorerAbortRef.current.abort();
    const ctrl = new AbortController();
    explorerAbortRef.current = ctrl;
    setExplorerLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });
    entities.forEach(n => params.append('entity_name', n));

    axios.get(`/api/field-intelligence/explorer?${params}`, { signal: ctrl.signal })
      .then(res => { if (id === explorerFetchIdRef.current) setExplorerData(res.data); })
      .catch(err => { if (id === explorerFetchIdRef.current && !axios.isCancel(err)) console.error('Error loading Entity Explorer:', err); })
      .finally(() => { if (id === explorerFetchIdRef.current) setExplorerLoading(false); });
  }, [filters]);

  const fetchEntityTrends = useCallback(() => {
    const id = ++trendFetchIdRef.current;
    if (trendAbortRef.current) trendAbortRef.current.abort();
    const ctrl = new AbortController();
    trendAbortRef.current = ctrl;
    setTrendLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });
    if (trendType) params.append('entity_type', trendType);
    if (trendEntities && trendEntities.length) params.append('entities', trendEntities.join(','));

    axios.get(`/api/field-intelligence/entity-trends?${params}`, { signal: ctrl.signal })
      .then(res => { if (id === trendFetchIdRef.current) setTrendData(res.data); })
      .catch(err => { if (id === trendFetchIdRef.current && !axios.isCancel(err)) console.error('Error loading entity trends:', err); })
      .finally(() => { if (id === trendFetchIdRef.current) setTrendLoading(false); });
  }, [filters, trendType, trendEntities]);

  // Scope changes (global filters, entity type) always need fresh data.
  useEffect(() => { fetchEntityTrends(); }, [filters, trendType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selection changes only need a fetch when a newly picked entity has no series loaded.
  // Removing a chip leaves a subset of what is already in trendData.trends, so it is a
  // pure re-render — no request, no spinner.
  useEffect(() => {
    if (trendEntities === null) return;              // handled by the scope effect above
    const loaded = trendData.selected || [];
    if (trendEntities.every(n => loaded.includes(n))) return;
    fetchEntityTrends();
  }, [trendEntities]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  // Re-run the explorer fetch when global filters change, but only if an entity is
  // already selected — otherwise there's nothing for it to fetch.
  useEffect(() => { if (selectedEntities.length) fetchExplorerData(selectedEntities); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Picking a type alone never hits the network — the Entity dropdown filters the
  // already-loaded name list client-side. A fetch only happens once an entity is chosen.
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setSelectedEntities([]);
    setExplorerData({ trend: [], byRegion: [], byCity: [], cooccurring: [] });
  };

  const handleEntityChange = (entities) => {
    setSelectedEntities(entities);
    if (entities.length) {
      fetchExplorerData(entities);
    } else {
      setExplorerData({ trend: [], byRegion: [], byCity: [], cooccurring: [] });
    }
  };

  const addWatchType = (type) => {
    const updated = [...watchTypes, type];
    setWatchTypes(updated);
    fetchData(updated);
  };

  const removeWatchType = (type) => {
    const updated = watchTypes.filter(t => t !== type);
    setWatchTypes(updated);
    fetchData(updated);
  };

  const { summary, entityTypes, rankedEntities,
    marketWatch = {}, explorer = {}, painSignals = [] } = pageData;

  // Panel tokens are qualified so types and curated groups can share one row of chips:
  // 'Product' is an entity_type, 'group:Fungicide' a group, 'unclassified:Product' the
  // leftovers. A bare name still means a type, which keeps older saved selections working.
  const availableGroups = pageData.availableGroups || [];
  const allTypes = entityTypes.map(t => t.entity_type);
  const groupTokens = availableGroups.map(g => `group:${g.group_name}`);
  // Unclassified is offered per root, never hidden: an entity in no group is exactly the
  // case that produced the "Ridomil Gold is missing" report, and it must stay reachable.
  const unclassifiedTokens = [...new Set(availableGroups.map(g => g.root_type))]
    .map(rt => `unclassified:${rt === 'Ingredient Type' ? 'Product' : 'Pest'}`);
  const allTokens = [...allTypes, ...groupTokens, ...unclassifiedTokens];
  const unselectedTypes = allTokens.filter(t => !watchTypes.includes(t));
  // Chips and headings show the readable half; the token keeps the qualifier.
  // tokenLabel comes from utils/entityLabels so Market Watch renames with everything
  // else — an 'Unclassified Product' column follows the same alias as the plain type.
  // Fall back to Region when this org has no separate city detail, so a mode left on
  // 'city' from a previous selection can't strand the panel with no way back.
  const effectiveLocationMode = explorerData.cityDistinct === false ? 'region' : locationMode;
  // Full rows, not bare names: the picker shows each entity's type and mention count.
  // The server already dropped everything with no mentions in the current scope, so
  // every option here is guaranteed to draw a non-empty panel.
  const explorerEntityOptions = (explorer.entityNames || [])
    .filter(e => !selectedType || e.entity_type === selectedType);

  // Narrowing the date range or region can remove an entity from the list entirely.
  // Drop any pick that is no longer on offer, otherwise it stays chipped and charted
  // against a scope that no longer contains it. Guarded on a loaded list so the first
  // render, when entityNames is still empty, cannot wipe a selection.
  useEffect(() => {
    const available = explorer.entityNames || [];
    if (!available.length || !selectedEntities.length) return;
    const names = new Set(available.map(e => e.entity_name));
    const kept = selectedEntities.filter(n => names.has(n));
    if (kept.length === selectedEntities.length) return;
    setSelectedEntities(kept);
    if (kept.length) fetchExplorerData(kept);
    else setExplorerData({ trend: [], byRegion: [], byCity: [], cooccurring: [] });
  }, [explorer.entityNames]); // eslint-disable-line react-hooks/exhaustive-deps

  // What the Explorer panels are about, as a heading: one name reads plainly, several
  // read as a pooled subject so nobody mistakes the single line for one entity.
  const explorerLabel = selectedEntities.length === 0
    ? 'Awaiting entity selection'
    : selectedEntities.length === 1
      ? selectedEntities[0]
      : `${selectedEntities.length} entities combined`;

  const topEntity = rankedEntities[0];
  const topType = entityTypes[0];
  const topPain = painSignals[0];

  // Multi-entity trend: pivot to one line per entity
  const trendRows = trendData.trends || [];
  const chartedEntities = trendEntities === null ? (trendData.selected || []) : trendEntities;
  const trendCandidates = trendData.available_entities || [];
  const trendTypeOptions = trendData.available_types || [];
  // Candidates already exclude anything with no mentions in the window, so the "add"
  // list never offers an entity that would draw a flat zero line.
  const addableEntities = trendCandidates.filter(c => !chartedEntities.includes(c.entity_name));
  const multiTrendPlotData = chartedEntities.map((name, i) => ({
    x: trendRows.filter(r => r.entity_name === name).map(r => r.week_start),
    y: trendRows.filter(r => r.entity_name === name).map(r => r.mention_count || 0),
    name, type: 'scatter', mode: 'lines+markers',
    line: { color: COLORS[i % COLORS.length], width: 2.5 },
  }));

  // This page counts only conversations that yielded entities, so its number is lower
  // than Home's "Total Conversations" over the same filters. State the coverage on the
  // card rather than leaving the customer to compare tabs and guess at the difference.
  const allConversations = summary.all_conversations ?? 0;
  const entityConversations = summary.total_conversations ?? 0;
  const entityCoverageNote = allConversations
    ? `of ${allConversations} conversations (${Math.round((entityConversations / allConversations) * 100)}%) had entities extracted`
    : 'With extracted entities';

  return (
    <div>
      {/* Header */}
      <div className="module-header">
        <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '6px' }}>
          Market Intelligence
        </div>
        <h2>Field Intelligence</h2>
        <p>A live market view of entity demand, clustering, product and service surfacing, and competitive presence.</p>
      </div>

      {/* Filter Bar */}
      <FilterBar />

      {loadError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '14px',
          padding: '14px 18px', marginBottom: '24px', fontSize: '13px', color: '#991b1b',
        }}>
          <strong>Couldn't load Field Intelligence data.</strong> {loadError} — try refreshing; if this
          persists, your session may need to be renewed (log out and back in).
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <KpiCard
          label="Conversations"
          value={summary.total_conversations ?? 0}
          note={entityCoverageNote}
          accent="blue"
        />
        <KpiCard label="Unique Entities" value={summary.unique_entities ?? 0} note="Distinct market references" accent="green" />
        <KpiCard label="Total Mentions" value={summary.total_mentions ?? 0} note="All entity references" accent="amber" />
      </div>

      {/* Insight Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InsightCard
          label="Market Lead"
          title={topEntity ? `${topEntity.entity_name} is the strongest live topic.` : 'No standout entity yet.'}
          body={topEntity
            ? `${topEntity.entity_name} is the most-mentioned entity in the selected scope — the clearest real-time demand anchor with ${topEntity.entity_total} references.`
            : 'Widen the current scope to surface a stronger demand leader.'}
        />
        <InsightCard
          label="Category Pressure"
          title={topType ? `${typeLabel(topType.entity_type)} leads the entity mix.` : 'No dominant category yet.'}
          body={topType
            ? `${typeLabel(topType.entity_type)} is generating the highest mention volume (${topType.mention_count}), suggesting conversation flow is clustering around that market category.`
            : 'Entity categories are too sparse in the current scope to identify a leader.'}
        />
        <InsightCard
          label="Primary Pain"
          title={topPain ? `${topPain.entity_name} is the strongest customer friction point.` : 'No dominant pain signal yet.'}
          body={topPain
            ? `${topPain.entity_name} is the most frequent pain pattern in the selected scope — the clearest live issue to address.`
            : 'Widen the current scope to surface a stronger customer pain pattern.'}
        />
      </div>

      {/* Entity Type Bars + Multi-Entity Trend */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <Panel loading={loading} title="Entity Volume by Type" subtitle="Market categories by mention volume (counted once per conversation)">
          {entityTypes.length ? (
            <Plot
              data={[{
                x: entityTypes.map(t => t.mention_count),
                y: entityTypes.map(t => typeLabel(t.entity_type)),
                type: 'bar', orientation: 'h',
                marker: { color: entityTypes.map((_, i) => COLORS[i % COLORS.length]) },
              }]}
              layout={{
                xaxis: { title: 'Mentions' },
                plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 20, b: 40, l: 140 }, height: 300,
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
              style={{ width: '100%' }}
            />
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>

        <Panel
          loading={false}
          title="Top Entity Trend Comparison"
          subtitle="Weekly movement across entities you choose (counted once per conversation)"
        >
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label htmlFor="fi-trend-type" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-light)', display: 'block', marginBottom: '6px' }}>
                Entity Type
              </label>
              <select
                id="fi-trend-type"
                value={trendType}
                /* Changing the type drops the explicit picks: they belong to the old type
                   and would just be filtered out server-side anyway. Empty selection means
                   the server seeds the new type's strongest entities. */
                onChange={e => { setTrendType(e.target.value); setTrendEntities(null); }}
                style={{ padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: '10px', background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '14px' }}
              >
                <option value="">All types</option>
                {trendTypeOptions.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="fi-trend-add" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-light)', display: 'block', marginBottom: '6px' }}>
                Add Entity
              </label>
              <select
                id="fi-trend-add"
                value=""
                disabled={!addableEntities.length || chartedEntities.length >= TREND_ENTITY_MAX}
                onChange={e => { if (e.target.value) setTrendEntities([...chartedEntities, e.target.value]); }}
                style={{ padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: '10px', background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '14px' }}
              >
                <option value="">
                  {chartedEntities.length >= TREND_ENTITY_MAX ? `Limit ${TREND_ENTITY_MAX} reached` : '— add to comparison —'}
                </option>
                {addableEntities.map(c => (
                  <option key={`${c.entity_type}|${c.entity_name}`} value={c.entity_name}>
                    {c.entity_name} ({c.mention_count})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {chartedEntities.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {chartedEntities.map((name, i) => (
                <button
                  key={name}
                  onClick={() => setTrendEntities(chartedEntities.filter(n => n !== name))}
                  title={`Remove ${name} from the comparison`}
                  style={{
                    padding: '4px 12px', fontSize: '12px', fontWeight: 600,
                    background: 'var(--bg-dark)', border: '1px solid var(--card-border)',
                    borderLeft: `3px solid ${COLORS[i % COLORS.length]}`,
                    borderRadius: '16px', cursor: 'pointer', color: 'var(--text-light)',
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  {name} <span style={{ fontSize: '14px', color: 'var(--text-lighter)' }}>×</span>
                </button>
              ))}
            </div>
          )}

          {trendLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '100px 0' }}><WaveLoader /></div>
          ) : multiTrendPlotData.length ? (
            <>
              <Plot
                data={multiTrendPlotData}
                layout={{
                  xaxis: { title: 'Week starting' }, yaxis: { title: 'Mentions' },
                  plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                  margin: { t: 20, r: 20, b: 50, l: 60 }, height: 300,
                  hovermode: 'x unified', legend: { orientation: 'h', y: -0.3 },
                }}
                config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
                style={{ width: '100%' }}
              />
              <WeekNote />
            </>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>
              {trendEntities && trendEntities.length === 0
                ? 'Add an entity above to compare its weekly trend.'
                : trendType
                  ? `No ${typeLabel(trendType)} mentions in the selected scope.`
                  : 'No data available'}
            </div>
          )}
        </Panel>
      </div>

      {/* Entity Explorer */}
      <div style={{ marginBottom: '32px' }}>
        <Panel loading={false} title="Entity Explorer" subtitle="Pick one or more entities to inspect trend, location spread, and co-occurrence — several are combined into a single view">
          {/* Controls */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-light)', display: 'block', marginBottom: '6px' }}>Entity Type</label>
              <select
                value={selectedType}
                onChange={e => handleTypeChange(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: '10px', background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '14px' }}
              >
                <option value="">All types</option>
                {(explorer.availableTypes || []).map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-light)', display: 'block', marginBottom: '6px' }}>Entity</label>
              <EntityMultiSelect
                options={explorerEntityOptions}
                selected={selectedEntities}
                onChange={handleEntityChange}
                max={EXPLORER_ENTITY_MAX}
              />
            </div>
          </div>

          {/* Explorer panels */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            <div style={{ background: 'var(--bg-dark)', border: '1px solid var(--card-border)', borderRadius: '20px', padding: '20px' }}>
              <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '8px' }}>
                Trend Over Time
              </div>
              <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '16px' }}>
                {explorerLabel}
              </div>
              {selectedEntities.length > 1 && (
                <div style={{ fontSize: '11px', color: 'var(--text-lighter)', marginTop: '-10px', marginBottom: '14px' }}>
                  One conversation mentioning several of them counts once.
                </div>
              )}
              {explorerLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}><WaveLoader size="small" /></div>
              ) : explorerData.trend?.length ? (
                <>
                  <Plot
                    data={[{
                      x: explorerData.trend.map(t => t.week_start),
                      y: explorerData.trend.map(t => t.conversation_count || 0),
                      type: 'scatter', mode: 'lines+markers',
                      line: { color: '#1f5eff', width: 2.5 },
                    }]}
                    layout={{
                      xaxis: { title: 'Week starting' }, yaxis: { title: 'Conversations' },
                      plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--bg-dark)',
                      margin: { t: 10, r: 10, b: 40, l: 40 }, height: 180, showlegend: false,
                    }}
                    config={{ responsive: true, displaylogo: false, displayModeBar: false }}
                    style={{ width: '100%' }}
                  />
                  <WeekNote />
                </>
              ) : (
                <div style={{ border: '1.5px dashed var(--card-border)', borderRadius: '12px', padding: '24px', textAlign: 'center', fontSize: '12px', color: 'var(--text-lighter)' }}>
                  Choose one or more entities to inspect their weekly trend.
                </div>
              )}
            </div>

            <div style={{ background: 'var(--bg-dark)', border: '1px solid var(--card-border)', borderRadius: '20px', padding: '20px' }}>
              <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '8px' }}>By Location</div>
              {/* Sibling panels in this grid lead with a 15px bold line; keeping one here
                  so the three columns still align. */}
              <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '12px' }}>
                {effectiveLocationMode === 'city' ? 'City distribution' : 'Region distribution'}
              </div>
              {/* Orgs that only ever collected one level of location detail get the same
                  list either way; the backend says so via cityDistinct and we drop the
                  toggle rather than offer a button that redraws the same chart. */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                {(explorerData.cityDistinct === false ? [['region', 'Region']]
                  : [['region', 'Region'], ['city', 'City']]).map(([mode, label]) => (
                  <button
                    key={mode}
                    onClick={() => setLocationMode(mode)}
                    style={{
                      padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                      borderRadius: '10px', border: '1px solid var(--card-border)', fontFamily: 'inherit',
                      background: effectiveLocationMode === mode ? 'var(--text-dark)' : 'var(--card-bg)',
                      color: effectiveLocationMode === mode ? 'var(--card-bg)' : 'var(--text-light)',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {explorerLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}><WaveLoader size="small" /></div>
              ) : (
                <SmallList
                  rows={(effectiveLocationMode === 'city' ? explorerData.byCity : explorerData.byRegion) || []}
                  nameKey={effectiveLocationMode === 'city' ? 'city' : 'region_name'}
                  countKey="mention_count"
                  subtitleKey=""
                  emptyLabel={`Choose one or more entities to see which ${effectiveLocationMode === 'city' ? 'cities' : 'regions'} mention them most.`}
                />
              )}
            </div>

            <div style={{ background: 'var(--bg-dark)', border: '1px solid var(--card-border)', borderRadius: '20px', padding: '20px' }}>
              <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '8px' }}>Co-occurring Entities</div>
              <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '16px' }}>Related entities by type</div>
              {explorerLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}><WaveLoader size="small" /></div>
              ) : cooccurringGroups.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {cooccurringGroups.map(([type, rows]) => (
                    <div key={type}>
                      <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--text-lighter)', marginBottom: '8px' }}>
                        {typeLabel(type)}
                      </div>
                      <SmallList rows={rows} nameKey="entity_name" countKey="mention_count" subtitleKey="" emptyLabel="" />
                    </div>
                  ))}
                </div>
              ) : (
                <SmallList
                  rows={[]}
                  emptyLabel="Choose one or more entities to see what else appears in the same conversations."
                />
              )}
            </div>
          </div>
        </Panel>
      </div>

      {/* Market Watch */}
      <Panel loading={loading} title="Market Watch" subtitle="Top entities by type (counted once per conversation) — click a type to add or remove it">
        {unselectedTypes.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
            {unselectedTypes.map(t => (
              <button
                key={t}
                onClick={() => addWatchType(t)}
                style={{
                  padding: '4px 12px', fontSize: '12px', fontWeight: 600,
                  background: 'var(--bg-dark)', border: '1px solid var(--card-border)',
                  borderRadius: '16px', cursor: 'pointer', color: 'var(--text-light)',
                }}
              >
                + {tokenLabel(t)}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(watchTypes.length, 4)}, 1fr)`, gap: '24px' }}>
          {watchTypes.map(wt => (
            <div key={wt}>
              <div
                onClick={() => removeWatchType(wt)}
                style={{
                  fontSize: '11px', fontWeight: 900, textTransform: 'uppercase',
                  letterSpacing: '0.22em', color: '#8b7355', marginBottom: '12px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                {tokenLabel(wt)} <span style={{ fontSize: '14px', color: 'var(--text-lighter)' }}>×</span>
              </div>
              <SmallList
                rows={(marketWatch[wt]?.rows || []).slice(0, 10)}
                nameKey="entity_name"
                countKey="mention_count"
                subtitleKey=""
                emptyLabel={`No ${tokenLabel(wt).toLowerCase()} signals detected.`}
              />
              {/* The panel shows the top few of what is often a very long tail. Saying so
                  is the whole point: with 555 products in one column and no note, an
                  entity at rank 23 reads as missing data rather than as ranked 23rd. */}
              {marketWatch[wt]?.total > (marketWatch[wt]?.rows || []).length && (
                <div style={{ fontSize: '11px', color: 'var(--text-lighter)', marginTop: '8px' }}>
                  top {(marketWatch[wt].rows || []).length} of {marketWatch[wt].total}
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

export default FieldIntel;
